import { useEffect, useState } from "react";
import { api, fmtDate, todayISO } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserPlus, Loader2, Edit, Shield } from "lucide-react";
import { useFilters } from "@/context/FiltersContext";
import { useAuth } from "@/context/AuthContext";

const emptyForm = {
  employee_id: "", name: "", role: "Field Executive", branch: "",
  joining_date: todayISO(), status: "Active", contact: "", remarks: "",
};

const ROLES = ["Field Executive", "Senior Field Executive", "Team Lead"];
const STATUSES = ["Active", "On Leave", "Inactive"];

export default function Executives() {
  const { user: currentUser } = useAuth();
  const { setExecutives } = useFilters();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/executives");
    setRows(r.data);
    setExecutives(r.data.filter((e) => e.status === "Active"));
    try {
      const u = await api.get("/users");
      setUsers(u.data);
    } catch { /* manager might not have access */ }
  };
  useEffect(() => { load(); }, []);

  const updateUserRole = async (u, field, value) => {
    try {
      const body = {};
      body[field] = value;
      await api.patch(`/users/${u.user_id}`, body);
      toast.success("Team access updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      employee_id: e.employee_id, name: e.name, role: e.role, branch: e.branch,
      joining_date: e.joining_date, status: e.status, contact: e.contact || "", remarks: e.remarks || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name || !form.employee_id) { toast.error("Employee ID & Name required"); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/executives/${editing.id}`, form);
      else await api.post("/executives", form);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="executives-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A24B]">Team Management</div>
          <h2 className="text-3xl font-light text-white mt-1" style={{ fontFamily: "Outfit" }}>
            Field Executive Master
          </h2>
          <p className="text-slate-400 text-sm mt-1">Add once — appears everywhere downstream automatically.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="btn-add-executive" onClick={openNew}
              className="bg-[#C9A24B] hover:bg-[#b08d40] text-[#0F172A] font-semibold rounded-lg px-4 py-2 text-sm flex items-center gap-2">
              <UserPlus size={16} /> Add Executive
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0F172A] border-slate-700 text-white max-w-xl">
            <DialogHeader><DialogTitle>{editing ? "Edit Executive" : "Add Field Executive"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["employee_id", "Employee ID"],
                ["name", "Full Name"],
                ["branch", "Location / City"],
                ["contact", "Contact Number"],
                ["joining_date", "Joining Date", "date"],
              ].map(([k, label, type]) => (
                <div key={k} className={k === "name" ? "col-span-2" : ""}>
                  <label className="text-xs text-slate-400">{label}</label>
                  <input data-testid={`exec-input-${k}`} type={type || "text"} value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    placeholder={k === "branch" ? "Delhi, Jaipur, Mumbai…" : undefined}
                    className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#C9A24B]" />
                </div>
              ))}
              <div>
                <label className="text-xs text-slate-400">Role</label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="exec-role" className="bg-slate-900 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0F172A] border-slate-700 text-white">
                    {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-400">Status</label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="exec-status" className="bg-slate-900 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0F172A] border-slate-700 text-white">
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-slate-400">Remarks</label>
                <textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                  className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-sm outline-none" rows={2} />
              </div>
            </div>
            <DialogFooter>
              <button data-testid="exec-save" onClick={submit} disabled={saving}
                className="bg-[#C9A24B] hover:bg-[#b08d40] text-[#0F172A] font-semibold rounded-lg px-4 py-2 flex items-center gap-2">
                {saving && <Loader2 size={14} className="animate-spin" />} Save
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Team Access — only visible to founder */}
      {currentUser?.role === "founder" && users.length > 0 && (
        <div className="bg-[#1E293B] border border-slate-800 rounded-xl overflow-hidden" data-testid="team-access-panel">
          <div className="p-4 border-b border-slate-800 flex items-center gap-2">
            <Shield size={16} className="text-[#C9A24B]" />
            <div>
              <div className="text-white font-medium" style={{ fontFamily: "Outfit" }}>Team Access &amp; Roles</div>
              <div className="text-xs text-slate-400 mt-0.5">
                When Sourabh or Pavan sign in with their Google account, come here to (a) set their role to <span className="text-[#C9A24B]">Executive</span> and (b) link them to their profile — after that they will only see their own tie-ups, logs and events.
              </div>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-800/40 text-slate-300 text-[11px] uppercase tracking-wider">
                {["User", "Email", "Role", "Linked Executive"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-t border-slate-800">
                  <td className="px-4 py-2.5 text-white font-medium flex items-center gap-2">
                    {u.picture ? <img src={u.picture} alt="" className="w-6 h-6 rounded-full" /> :
                      <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-[10px] text-white">{u.name?.[0]}</div>}
                    {u.name}
                  </td>
                  <td className="px-4 py-2.5 text-slate-300">{u.email}</td>
                  <td className="px-4 py-2.5">
                    <Select value={u.role} onValueChange={(v) => updateUserRole(u, "role", v)} disabled={u.email === currentUser.email}>
                      <SelectTrigger className="bg-slate-900 border-slate-700 h-8 w-36" data-testid={`role-${u.user_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0F172A] border-slate-700 text-white">
                        <SelectItem value="founder">Founder</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="executive">Executive</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-2.5">
                    <Select
                      value={u.executive_id || "__none__"}
                      onValueChange={(v) => updateUserRole(u, "executive_id", v === "__none__" ? "" : v)}
                    >
                      <SelectTrigger className="bg-slate-900 border-slate-700 h-8 w-56" data-testid={`link-${u.user_id}`}>
                        <SelectValue placeholder="Not linked" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0F172A] border-slate-700 text-white">
                        <SelectItem value="__none__">Not linked</SelectItem>
                        {rows.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-[#1E293B] border border-slate-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-800/50 text-slate-300 text-[11px] uppercase tracking-wider">
              {["Employee ID", "Name", "Role", "Location", "Joining", "Status", "Contact", "Remarks", ""].map((h) => (
                <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.id} className="border-t border-slate-800 hover:bg-slate-800/30">
                <td className="px-4 py-2.5 text-slate-200 mono">{e.employee_id}</td>
                <td className="px-4 py-2.5 text-white font-medium">{e.name}</td>
                <td className="px-4 py-2.5">{e.role}</td>
                <td className="px-4 py-2.5">
                  <span className="inline-flex items-center gap-1.5 text-xs bg-[#C9A24B]/10 border border-[#C9A24B]/25 text-[#C9A24B] px-2 py-0.5 rounded-full">
                    {e.branch || "—"}
                  </span>
                </td>
                <td className="px-4 py-2.5">{fmtDate(e.joining_date)}</td>
                <td className="px-4 py-2.5">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                    e.status === "Active" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" :
                    e.status === "On Leave" ? "bg-amber-500/15 text-amber-400 border-amber-500/30" :
                    "bg-slate-500/15 text-slate-400 border-slate-500/30"
                  }`}>{e.status}</span>
                </td>
                <td className="px-4 py-2.5">{e.contact || "—"}</td>
                <td className="px-4 py-2.5 text-xs text-slate-400 max-w-xs truncate">{e.remarks || "—"}</td>
                <td className="px-4 py-2.5">
                  <button data-testid={`edit-exec-${e.id}`} onClick={() => openEdit(e)} className="text-[#C9A24B] hover:text-[#b08d40]">
                    <Edit size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
